/* eslint-disable indent */
/* eslint-disable eqeqeq */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import { DateTime, Duration } from 'luxon';
import { ActivityRecordResponse } from './models/response/activity-record.response';
import { CalendarService } from './calendar.service';
import { EventInput } from '@fullcalendar/core';
import { AlertService } from '@shared//services/alert/alert.service';
import {
  durationToString,
  stripTime,
  stripTimeZone,
  timeStringToDuration,
} from '@shared//utilities/utilites';
import { getBidMonthStartDate } from '@shared/utilities/utilites';
import { RequestState } from '../shared/models/request-state';
import { DataItem } from '../shared/models/data-item';
import { Injectable } from '@angular/core';
import { CalendarView } from './models/view/calendar.view';
import { RouteParameterStore } from '@shared/services/route-parameter.store';
import { ActivityItemResponse } from './models/response/activity-item.response';
import { SequenceRequest } from '@sequence/models/request/sequence.request';
import { SequenceView } from '@sequence/models/view/sequence.view';

interface PrevSelectedMonth {
  date: string;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarStore {
  private readonly DFP_COLOR = '#33a84c';
  private readonly MISC_COLOR = '#f1a557';
  private readonly SEQ_COLOR = '#1c80cf';
  private readonly RAP_COLOR = '#63939a';

  // Selected month as ISO date string (yyyy-mm-dd). Updated when the user selects a month on the calendar.
  // Triggers calendarEvents$ to update. Starts with today's date.
  private _selectedMonth = new BehaviorSubject<string | null>(null);

  // Triggers a manual refresh.
  private _forceRefresh = false;

  // State of DFPs (duty-free periods) toggle.
  private _showDfps = new BehaviorSubject<boolean>(false);

  private _isAdminMode = new BehaviorSubject<boolean>(false);

  // Persistent settings/actions.
  private readonly STORAGE_KEYS = {
    showDfps: 'calendar.showDfps',
    prevSelectedMonth: 'calendar.prevSelectedMonth',
  };

  selectedMonth$ = this._selectedMonth.asObservable();
  showDfps$ = this._showDfps.asObservable();
  isAdminMode$ = this._isAdminMode.asObservable();
  calendar$ = this.createCalendarView();

  constructor(
    private calendarService: CalendarService,
    private alertService: AlertService,
    private routeParamStore: RouteParameterStore,
  ) {
    this.loadSavedSettings();
  }

  /** Get persistent data from local storage. */
  loadSavedSettings(): void {
    // Show DFPs setting.
    const showDfps = localStorage.getItem(this.STORAGE_KEYS.showDfps);
    if (showDfps !== null) {
      this._showDfps.next(JSON.parse(showDfps));
    }
  }

  getInitialDate(): string {
    // Previously selected month. So that when navigating back to the calendar,
    // the user is taken back to the month they were viewing.
    const prevSelectedMonth = localStorage.getItem(
      this.STORAGE_KEYS.prevSelectedMonth,
    );

    if (prevSelectedMonth) {
      const selectedMonth = JSON.parse(prevSelectedMonth) as PrevSelectedMonth;

      // Only use previously saved month if the timestamp is recent (less than two days ago).
      // This ensures the user is taken to the current month if they haven't opened the app
      // for a while. diffNow() ends up negative, hence the * -1.
      const age =
        DateTime.fromISO(selectedMonth.timestamp).diffNow('days').days * -1;

      if (age < 2) {
        return selectedMonth.date;
      }
    }

    return DateTime.now().toISODate();
  }

  private saveSelectedMonth(date: string): void {
    const selectedMonth: PrevSelectedMonth = {
      date,
      timestamp: DateTime.now().toISO(),
    };
    localStorage.setItem(
      this.STORAGE_KEYS.prevSelectedMonth,
      JSON.stringify(selectedMonth),
    );
  }

  /** Updates selected month observable. Set when the calendar month is changed. */
  setSelectedMonth(date: string): void {
    this.saveSelectedMonth(date);
    this._selectedMonth.next(this.normalizeSelectedMonth(date));
  }

  forceRefresh(): void {
    this._forceRefresh = true;
    const selectedMonth = this._selectedMonth.getValue();

    // Trigger refresh. _forceRefresh flag will get reset automatically.
    if (selectedMonth) {
      this.setSelectedMonth(selectedMonth);
    }
  }

  /** Update observable and save to local storage. */
  setShowDfps(showDfps: boolean): void {
    this._showDfps.next(showDfps);
    localStorage.setItem(this.STORAGE_KEYS.showDfps, JSON.stringify(showDfps));
  }

  /** Get the specified activity record from API or cache when selected month changes. */
  private getActivityRecord(): Observable<
    RequestState<ActivityRecordResponse>
  > {
    return this.selectedMonth$.pipe(
      filter((selectedMonth) => selectedMonth !== null),
      switchMap((selectedMonth) => {
        if (this._forceRefresh) {
          this._forceRefresh = false;

          if (this.routeParamStore.calendarEmployeeNumber) {
            this._isAdminMode.next(true);
          }

          return this.calendarService
            .refreshActivityRecord(
              selectedMonth!,
              this.routeParamStore.calendarEmployeeNumber || undefined,
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert();
                }
              }),
            );
        } else {
          return this.calendarService
            .getActivityRecord(
              selectedMonth!,
              this.routeParamStore.calendarEmployeeNumber || undefined,
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert();
                }
              }),
            );
        }
      }),
    );
  }

  /** Find next flight on schedule. */
  getNextFlightUrl(): string | null {
    const nextSequence = this.getNextSequence();

    if (nextSequence?.linkedSequence) {
      // Convert to sequence view. This will provide the isNext flag to easily find the next flight.
      const seqView = new SequenceView(nextSequence.linkedSequence);

      for (const dp of seqView.dutyPeriods) {
        for (const leg of dp.legs) {
          if (leg.isNextLeg) {
            return `/sequence/${seqView.sequenceNumber}/${
              nextSequence.positionCode
            }/${stripTime(nextSequence.originationDate)}/flight/${
              leg.flightNumber
            }/${leg.depStation}/${
              leg.arrStation
            }/${leg.depTimeSched?.toISODate()}`;
          }
        }
      }

      // If no next leg, return last leg of this sequence.
      for (const dp of seqView.dutyPeriods.reverse()) {
        for (const leg of dp.legs.reverse()) {
          return `/sequence/${seqView.sequenceNumber}/${
            nextSequence.positionCode
          }/${stripTime(nextSequence.originationDate)}/flight/${
            leg.flightNumber
          }/${leg.depStation}/${
            leg.arrStation
          }/${leg.depTimeSched?.toISODate()}`;
        }
      }
    }

    return null;
  }

  /** Returns the sequence number, position and date for navigation. */
  getNextSequenceProperties(): SequenceRequest | null {
    const nextSequence = this.getNextSequence();

    if (nextSequence) {
      return {
        number: nextSequence.activityIdentifier.toString(),
        date: stripTime(nextSequence.originationDate),
        seat: nextSequence.positionCode,
      };
    }

    return null;
  }

  /**
   * Find the current or next sequence on pilot's schedule. Uses cached activity records.
   */
  getNextSequence(): ActivityItemResponse | null {
    const today = DateTime.now();
    // Mostly used for testing. Use empnum if in admin mode.
    const empNum = this.routeParamStore.calendarEmployeeNumber
      ? `/${this.routeParamStore.calendarEmployeeNumber}`
      : '';
    const currentMonthDate = today.toFormat('yyyy-MM-15');
    // console.log(currentMonthDate);
    const thisMonthsActivity = localStorage.getItem(
      `activityRecord-${currentMonthDate}${empNum}`,
    );

    if (thisMonthsActivity) {
      const activity = JSON.parse(thisMonthsActivity) as ActivityRecordResponse;
      const nextSeqThisMonth = this.findNextSeqInActivity(activity);
      if (nextSeqThisMonth) {
        return nextSeqThisMonth;
      }
    }

    // If next sequence not found in current month, look at next month.
    const nextMonthDate = DateTime.fromISO(currentMonthDate)
      .plus({ months: 1 })
      .set({ day: 15 });
    const nextMonthsActivity = localStorage.getItem(
      `activityRecord-${nextMonthDate.toFormat('yyyy-MM-dd')}`,
    );

    if (nextMonthsActivity) {
      const activity = JSON.parse(nextMonthsActivity) as ActivityRecordResponse;
      const nextSeqNextMonth = this.findNextSeqInActivity(activity);
      if (nextSeqNextMonth) {
        return nextSeqNextMonth;
      }
    }

    return null;
  }

  /** Helper for getNextSequence(). Finds next sequence in given activity record. */
  private findNextSeqInActivity(
    activity: ActivityRecordResponse,
  ): ActivityItemResponse | null {
    // List of sequences that aren't removals ordered descending.
    const sortedSequences = activity.activityItems.filter(
      (item) =>
        item.activityType === 'SEQ' &&
        item.removalCode.trim() === '' &&
        !item.linkedSequence?.sequenceComplete,
    );

    if (sortedSequences.length > 0) {
      return sortedSequences[0];
    }

    return null;
  }

  /** Get previous month's activity record from API or cache when selected month changes. */
  private getActivityRecordPrev(): Observable<
    RequestState<ActivityRecordResponse>
  > {
    return this.selectedMonth$.pipe(
      filter((selectedMonth) => selectedMonth !== null),
      switchMap((selectedMonth) => {
        if (this._forceRefresh) {
          this._forceRefresh = false;

          if (this.routeParamStore.calendarEmployeeNumber) {
            this._isAdminMode.next(true);
          }

          const prevMonth = DateTime.fromISO(selectedMonth || '')
            .minus({ month: 1 })
            .toISODate();

          return this.calendarService
            .refreshActivityRecord(
              prevMonth,
              this.routeParamStore.calendarEmployeeNumber || undefined,
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert();
                }
              }),
            );
        } else {
          const prevMonth = DateTime.fromISO(selectedMonth || '')
            .minus({ month: 1 })
            .toISODate();

          return this.calendarService
            .getActivityRecord(
              prevMonth,
              this.routeParamStore.calendarEmployeeNumber || undefined,
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert();
                }
              }),
            );
        }
      }),
    );
  }

  /** Get next month's activity record from API or cache when selected month changes. */
  private getActivityRecordNext(): Observable<
    RequestState<ActivityRecordResponse>
  > {
    return this.selectedMonth$.pipe(
      filter((selectedMonth) => selectedMonth !== null),
      switchMap((selectedMonth) => {
        if (this._forceRefresh) {
          this._forceRefresh = false;

          if (this.routeParamStore.calendarEmployeeNumber) {
            this._isAdminMode.next(true);
          }

          const nextMonth = DateTime.fromISO(selectedMonth || '')
            .plus({ month: 1 })
            .toISODate();

          return this.calendarService
            .refreshActivityRecord(
              nextMonth,
              this.routeParamStore.calendarEmployeeNumber || undefined,
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert();
                }
              }),
            );
        } else {
          const nextMonth = DateTime.fromISO(selectedMonth || '')
            .plus({ month: 1 })
            .toISODate();

          return this.calendarService
            .getActivityRecord(
              nextMonth,
              this.routeParamStore.calendarEmployeeNumber || undefined,
            )
            .pipe(
              tap((activityRecordState) => {
                if (activityRecordState.hasError) {
                  this.showErrorAlert();
                }
              }),
            );
        }
      }),
    );
  }

  /** Generate events displayed on calendar. Updates when activityRecord$ or showDfps$ changes. */
  private createCalendarView(): Observable<RequestState<CalendarView>> {
    return combineLatest([
      this.showDfps$,
      this.getActivityRecord(),
      this.getActivityRecordPrev(),
      this.getActivityRecordNext(),
    ]).pipe(
      map(
        ([
          showDfps,
          activityRecordState,
          prevActivityRecordState,
          nextActivityRecordState,
        ]) => {
          // Pass on other state (isLoading, hasError) if no response. Only uses selected month activity
          // record for loading/error state.
          if (activityRecordState.hasError) {
            this.showErrorAlert();
          }

          const activityRecord = activityRecordState.response;
          const thisMonthSeqEvents = this.createSequenceEvents(activityRecord);
          let thisMonthNonSeqEvents =
            this.createNonSequenceEvents(activityRecord);
          const rapEvents = this.createRapEvents(activityRecord);
          // const sevenDEvents = this.create7DEvents(activityRecord);
          thisMonthNonSeqEvents = thisMonthNonSeqEvents.concat(rapEvents);

          const monthlyTotals = this.getMonthlyTotals(activityRecord);

          // Prev month events
          let prevMonthNonSeqEvents: EventInput[] = [];
          let prevMonthSeqEvents: EventInput[] = [];

          if (prevActivityRecordState.response) {
            prevMonthSeqEvents = this.createSequenceEvents(
              prevActivityRecordState.response,
            );
            prevMonthNonSeqEvents = this.createNonSequenceEvents(
              prevActivityRecordState.response,
            );
          }

          // Next month events
          let nextMonthNonSeqEvents: EventInput[] = [];
          let nextMonthSeqEvents: EventInput[] = [];

          if (nextActivityRecordState.response) {
            nextMonthSeqEvents = this.createSequenceEvents(
              nextActivityRecordState.response,
            );
            nextMonthNonSeqEvents = this.createNonSequenceEvents(
              nextActivityRecordState.response,
            );
          }

          const selectedMonthDate = DateTime.fromISO(
            this._selectedMonth.getValue() || '',
          );
          const prevMonth = selectedMonthDate.minus({ months: 1 });
          const nextMonth = selectedMonthDate.plus({ months: 1 });

          const holidayEventsPrevMonth = this.createHolidayPayEvents(
            prevMonth.month,
            prevMonth.year,
          );
          const holidayEventsThisMonth = this.createHolidayPayEvents(
            selectedMonthDate.month,
            selectedMonthDate.year,
          );
          const holidayEventsNextMonth = this.createHolidayPayEvents(
            nextMonth.month,
            nextMonth.year,
          );

          // Combine and filter prev month, this month, and next month events.
          const nonSeqEvents = [
            ...prevMonthNonSeqEvents,
            ...thisMonthNonSeqEvents,
            ...nextMonthNonSeqEvents,
          ];

          const seqEvents = [
            ...prevMonthSeqEvents,
            ...thisMonthSeqEvents,
            ...nextMonthSeqEvents,
          ];

          let events = this.combineAndFilterEvents(
            seqEvents,
            nonSeqEvents,
            showDfps,
          );

          events = [
            ...events,
            ...holidayEventsPrevMonth,
            ...holidayEventsThisMonth,
            ...holidayEventsNextMonth,
          ];

          const calendarView: CalendarView = {
            events,
            monthlyTotals,
            timestamp: DateTime.fromISO(activityRecord?.timestamp || '', {
              zone: 'utc',
            }),
          };

          // Return response with other state passed through.
          return activityRecordState.into<CalendarView>(calendarView);
        },
      ),
    );
  }

  private createSequenceEvents(
    activityRecord?: ActivityRecordResponse | null,
  ): EventInput[] {
    const events: EventInput[] = [];

    if (!activityRecord) {
      return events;
    }

    for (const item of activityRecord.activityItems) {
      if (
        ((item.activityType === 'SEQ' && item.removalCode.trim() === '') ||
          item.activityType === 'CRR') &&
        !this.isTrainingRemovalAndAdd(item, activityRecord)
      ) {
        events.push(this.createEventFromSequence(item));
      }
    }

    return events;
  }

  /**
   * When a pilot has a leg(s) removed for training, scheduling removes the sequence with remove code DT
   * then adds the same sequence with add code RA. This method detects this scenario to prevent an uneccesary
   * DT removal event from showing on the calendar.
   */
  private isTrainingRemovalAndAdd(
    sequence: ActivityItemResponse,
    activityRecord: ActivityRecordResponse,
  ): boolean {
    if (sequence.removalCode === 'DT') {
      const sequenceNumber = sequence.activityIdentifier;

      for (const item of activityRecord.activityItems) {
        if (
          item.activityIdentifier === sequenceNumber &&
          item.addCode === 'RA'
        ) {
          return true;
        }
      }
    }
    return false;
  }

  /** Combines events, remove any duplicates, and filter based on showDfps. */
  private combineAndFilterEvents(
    sequenceEvents: EventInput[],
    nonSequenceEvents: EventInput[],
    showDfps: boolean,
  ): EventInput[] {
    const uniqueEvents = this.appendUniqueNonSequenceEvents(
      sequenceEvents,
      nonSequenceEvents,
    );

    const events: EventInput[] = uniqueEvents.filter((event) => {
      if (event.title && event.title !== '') {
        if (!showDfps) {
          return (
            event.title !== '24' &&
            event.title !== 'M2' &&
            event.title !== 'G2' &&
            event.title !== 'X2'
          );
        } else {
          return true;
        }
      }
      return false;
    });

    return events;
  }

  /** Appends non-sequence events to sequence events array, but only if the non-sequence event doesn't overlap the sequence event. */
  private appendUniqueNonSequenceEvents(
    seqEvents: EventInput[],
    nonSeqEvents: EventInput[],
  ): EventInput[] {
    // Create DateTime objects from date strings for date comparisons.
    const seqEventsWithDate = seqEvents
      .map((event) => ({
        ...event,
        data: {
          start: DateTime.fromISO(event.start?.toString() || '', {
            zone: 'utc',
          }),
          // End date has 1 day added for calendar display purposes, need to remove it.
          end: DateTime.fromISO(event.end?.toString() || '', {
            zone: 'utc',
          }).minus({ days: 1 }),
        },
      }))
      .filter((event) => event.title);

    const nonSeqEventsWithDate = nonSeqEvents
      .map((event) => ({
        ...event,
        data: {
          start: DateTime.fromISO(event.start?.toString() || '', {
            zone: 'utc',
          }),
        },
      }))
      .filter((event) => event.title);

    // Compare event dates and remove duplicates. The sequence-based events take priority.
    const filteredNonSeqEvents = nonSeqEventsWithDate.filter((nonSeqEvent) => {
      for (const seqEvent of seqEventsWithDate) {
        if (
          nonSeqEvent.data.start.diff(seqEvent.data.start, 'days').days >= -1 &&
          nonSeqEvent.data.start.diff(seqEvent.data.end, 'days').days <= 0
        ) {
          return false;
        }
      }
      return true;
    });

    return seqEvents.concat(filteredNonSeqEvents);
  }

  /** Convert monthly totals into DataItem[] for display. */
  private getMonthlyTotals(
    activity?: ActivityRecordResponse | null,
  ): DataItem[] {
    const monthlyTotals: DataItem[] = [];

    // Times need converted from 'd.hh:mm:ss' to 'hh:mm'
    const proj = timeStringToDuration(
      activity?.dutyTime.actualProjectionTime || '',
    ).toFormat('hh:mm');

    const pproj = timeStringToDuration(
      activity?.dutyTime.payProjectionTime || '',
    ).toFormat('hh:mm');

    const gtd = timeStringToDuration(
      activity?.dutyTime.flownToDate || '',
    ).toFormat('hh:mm');

    const imax = durationToString(
      timeStringToDuration(activity?.dutyTime.maxCreditProjectionTime || ''),
    );

    monthlyTotals.push({
      label: 'PROJ',
      value: proj,
    });

    monthlyTotals.push({
      label: 'PPROJ',
      value: pproj,
    });

    monthlyTotals.push({
      label: 'GTD',
      value: gtd,
    });

    monthlyTotals.push({
      label: 'IMAX',
      value: imax,
    });

    return monthlyTotals;
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to load schedule information.',
    });
  }

  /** Use a consistent day for request caching. */
  private normalizeSelectedMonth(selectedMonth: string): string {
    return DateTime.fromISO(selectedMonth).set({ day: 15 }).toISODate();
  }

  /** Create observable of calendar events for activities other than sequences. */
  private createNonSequenceEvents(
    activityRecord?: ActivityRecordResponse | null,
  ): EventInput[] {
    if (!activityRecord) {
      return [];
    }

    // Use hashmap with date index to ensure no duplicate events per day. 24s are occasionally duplicated.
    const events: { [key: string]: EventInput } = {};

    // const dutyArrayEvents = this.createEventsFromDutyArray(activityRecord);
    const plannedAbsenceEvents =
      this.createPlannedAbsenceEvents(activityRecord);

    for (const plannedAbsenceEvent of plannedAbsenceEvents) {
      events[plannedAbsenceEvent.start?.toString() || ''] = plannedAbsenceEvent;
    }

    // for (const dutyArrayEvent of dutyArrayEvents) {
    //   events[dutyArrayEvent.start?.toString() || ''] = dutyArrayEvent;
    // }

    for (const item of activityRecord.activityItems) {
      if (
        item.activityType !== 'SEQ' &&
        !item.linkedSequence &&
        item.activityType !== 'MSC' &&
        item.activityType !== 'PRV' &&
        item.removalCode.trim() === ''
      ) {
        const startTime = DateTime.fromISO(item.originationDate, {
          zone: 'utc',
        });
        const endTime = this.getEndDateTime(
          item.originationDate,
          this.convertDuration(item.duration),
        ).minus({ minutes: 1 }); // Subtract one minute so that 24 hr events display properly.

        const startAndEndMarginClasses = this.getEventMarginClass(
          startTime,
          endTime,
        );

        // If an event already exists for this date, don't overwrite if the new event is a 24.
        if (
          !(
            // substring grabs the date portion of ISO string, disregarding time if it exists.
            (
              events[item.originationDate.substring(0, 10)] &&
              item.activityIdentifier === 24
            )
          )
        ) {
          events[item.originationDate.substring(0, 10)] = {
            id: `${item.originationDate} ${item.activityType}`,
            title: this.convertDisplayedActivityType(item.activityType),
            start: item.originationDate,
            end: endTime.plus({ days: 1 }).toISO(),
            allDay: true,
            backgroundColor: this.DFP_COLOR,
            className: `${startAndEndMarginClasses.startClass} ${startAndEndMarginClasses.endClass}`,
          };
        }
      }
    }

    const eventsArray = Object.keys(events).map((date) => events[date]);

    return eventsArray;
  }

  private createRapEvents(
    activityRecord?: ActivityRecordResponse | null,
  ): EventInput[] {
    if (!activityRecord) {
      return [];
    }

    const rapEvents: EventInput[] = [];
    // Used to track duplicates.
    const rapIndices: number[] = [];

    if (activityRecord.reserveAvailabilityItems) {
      for (const rapItem of activityRecord.reserveAvailabilityItems) {
        if (!rapIndices.includes(rapItem.rapindex)) {
          const rapStartHour = rapItem.startTime.substring(11, 13);
          const rapStartMinutes = rapItem.startTime.substring(14, 16);
          const rapEndHour = rapItem.endTime.substring(11, 13);
          const rapEndMinutes = rapItem.endTime.substring(14, 16);

          rapEvents.push({
            title: `${rapStartHour}${rapStartMinutes} RAP`,
            date: rapItem.startTime,
            allDay: true,
            backgroundColor: this.RAP_COLOR,
            extendedProps: {
              rapStart: `${rapStartHour}:${rapStartMinutes}`,
              rapEnd: `${rapEndHour}:${rapEndMinutes}`,
            },
          });

          rapIndices.push(rapItem.rapindex);
        }
      }
    }

    return rapEvents;
  }

  // private create7DEvents(activityRecord: ActivityRecordResponse): EventInput[] {
  //   const events: EventInput[] = [];

  //   for (const item of activityRecord.obligationItems) {
  //     if (item.type === '7D') {
  //       const startAndEndMarginClasses = this.getEventMarginClass(
  //         DateTime.fromISO(item.startDateTime, {zone: 'utc'}),
  //         DateTime.fromISO(item.endDateTime, {zone: 'utc'})
  //       );

  //       events.push({
  //         id: `${item.startDateTime} 7D`,
  //         title: '7D',
  //         start: item.startDateTime,
  //         end: DateTime.fromISO(item.endDateTime, {zone: 'utc'}).plus({ days: 1 }).toISO(),
  //         allDay: true,
  //         backgroundColor: this.MISC_COLOR,
  //         className: `${startAndEndMarginClasses.startClass} ${startAndEndMarginClasses.endClass}`,
  //       });
  //     }
  //   }

  //   return events;
  // }

  /** Returns a calendar sequence event from the activity item when sequence data is missing. */
  private createEventFromSequenceWithMissingSeqData(
    activityItem: ActivityItemResponse,
  ): EventInput {
    const position = activityItem.positionCode;
    const removeCode = activityItem.removalCode.trim();
    const sequence = activityItem.linkedSequence;

    const sequenceStartTime = DateTime.fromISO(
      activityItem.startDateTimeLocal,
      { zone: 'utc' },
    );
    const sequenceEndTime = DateTime.fromISO(activityItem.endDateTimeLocal, {
      zone: 'utc',
    });

    // To keep consistent, pad starting zeroes to sequence number.
    let title = activityItem.activityIdentifier.toString().padStart(5, '0');
    let backgroundColor = this.SEQ_COLOR;

    // For 777/787 FOs, append seat (FO/FB/FC) to title.
    if (position === 'FB' || position === 'FC') {
      title += ` ${position}`;
    }

    if (removeCode !== '') {
      title = removeCode;
      backgroundColor = this.MISC_COLOR;
    }

    const startAndEndMarginClasses = this.getEventMarginClass(
      sequenceStartTime,
      sequenceEndTime,
    );

    const isSeqDataMissing =
      !activityItem.linkedSequence ||
      activityItem.linkedSequence.dutyPeriodSummaries.length === 0;

    return {
      title,
      start: stripTimeZone(sequenceStartTime.toISO()),
      // FullCalendar excludes the end day, so add another day to display correctly.
      end: stripTimeZone(sequenceEndTime.plus({ days: 1 }).toISO()),
      allDay: true,
      backgroundColor,
      // 'complete' class makes finished sequences faded. Make sure title is long enough to be a sequence.
      className: `${
        sequence?.sequenceComplete && title && title.length > 2
          ? 'complete'
          : ''
      } ${startAndEndMarginClasses.startClass} ${
        startAndEndMarginClasses.endClass
      }`,
      extendedProps: {
        sequenceNumber: activityItem.activityIdentifier,
        position: activityItem.positionCode,
        // The date that will be used for sequence request needs to match date from activity record.
        startDate: activityItem.originationDate,
        isSeqDataMissing,
      },
    };
  }

  /** Returns a calendar event from the sequence information. */
  private createEventFromSequence(
    activityItem: ActivityItemResponse,
  ): EventInput {
    const sequence = activityItem.linkedSequence;
    const position = activityItem.positionCode;
    const removeCode = activityItem.removalCode.trim();

    // If sequence data exists, the start and end time are more accurate and will be used.
    // If sequence data is missing, use the less accurate activity start and end times.
    if (!sequence || sequence.dutyPeriodSummaries.length === 0) {
      return this.createEventFromSequenceWithMissingSeqData(activityItem);
    }

    const dutyPeriods = sequence.dutyPeriodSummaries;

    // Use duty period start/end times for more accurate times.
    if (dutyPeriods.length > 0) {
      const firstDutyPeriod = dutyPeriods[0];
      const lastDutyPeriod = dutyPeriods[dutyPeriods.length - 1];

      if (
        !firstDutyPeriod.dutyPeriodStartTime ||
        !lastDutyPeriod.dutyPeriodEndTime
      ) {
        return {};
      }

      let sequenceStartTime = DateTime.fromISO(
        firstDutyPeriod.dutyPeriodStartTime || '',
        { zone: 'utc' },
      );
      let sequenceEndTime = DateTime.fromISO(
        lastDutyPeriod.dutyPeriodEndTime || '',
        { zone: 'utc' },
      );

      // For credited removals, accurate start/end times aren't important. It's better to display the original
      // start/end times from the activity data.
      if (activityItem.activityType === 'CRR') {
        sequenceStartTime = DateTime.fromISO(activityItem.startDateTimeLocal, {
          zone: 'utc',
        });
        sequenceEndTime = DateTime.fromISO(activityItem.endDateTimeLocal, {
          zone: 'utc',
        });
      }

      let title = sequence.sequenceNumber;
      let backgroundColor = this.SEQ_COLOR;

      // For 777/787 FOs, append seat (FO/FB/FC) to title.
      if (sequence.sequenceNumber && (position === 'FB' || position === 'FC')) {
        title += ` ${position}`;
      }

      if (removeCode !== '') {
        title = removeCode;
        backgroundColor = this.MISC_COLOR;
      }

      const startAndEndMarginClasses = this.getEventMarginClass(
        sequenceStartTime,
        sequenceEndTime,
      );

      return {
        title,
        start: firstDutyPeriod.dutyPeriodStartTime,
        // FullCalendar excludes the end day, so add another day to display correctly.
        end: stripTimeZone(sequenceEndTime.plus({ days: 1 }).toISO()),
        allDay: true,
        backgroundColor,
        // 'complete' class makes finished sequences faded. Make sure title is long enough to be a sequence.
        className: `${
          sequence.sequenceComplete && title && title.length > 2
            ? 'complete'
            : ''
        } ${startAndEndMarginClasses.startClass} ${
          startAndEndMarginClasses.endClass
        }`,
        extendedProps: {
          sequenceNumber: sequence.sequenceNumber,
          position: position,
          // The date that will be used for sequence request needs to match date from activity record.
          startDate: activityItem.originationDate,
        },
      };
    }

    return {};
  }

  /** This replaces the createEventsFromDutyArray function. Creates the same events from a different source. */
  private createPlannedAbsenceEvents(
    activityRecord: ActivityRecordResponse,
  ): EventInput[] {
    const { period, year } = activityRecord.effectivePeriod;

    // Many bid months don't start on the first of the month.
    const periodStartDate = getBidMonthStartDate(period, year);

    if (!periodStartDate) {
      console.log('Invalid period start date.');
      return [];
    }

    const dutyEvents: EventInput[] = [];

    for (const absence of activityRecord.multiplePlannedAbsences) {
      const { date, absenceCode } = absence;

      dutyEvents.push({
        title: absenceCode,
        start: date,
        allDay: true,
        backgroundColor: this.MISC_COLOR,
      });
    }

    return dutyEvents;
  }

  /**
   * Create events from the dailyActivity_DutyArray.
   * Ex: "DODODODO0DDODOPUPUPUPUDODOPUPUPUPUDODODODODODODODOPUPUPUPUDODO"
   * Every two chars represents a day (DO, PU, etc.). Contains vacation, training, and
   * removals not found elsewhere in the activity record.
   */
  private createEventsFromDutyArray(
    activityRecord: ActivityRecordResponse,
  ): EventInput[] {
    const { period, year } = activityRecord.effectivePeriod;
    const { dailyActivity_DutyArray } = activityRecord.activityHeader;

    // Many bid months don't start on the first of the month.
    const periodStartDate = getBidMonthStartDate(period, year);

    if (!periodStartDate) {
      console.log('Invalid period start date.');
      return [];
    }

    const dutyEvents: EventInput[] = [];

    for (let i = 0; i < dailyActivity_DutyArray.length / 2; i++) {
      const activityCode = dailyActivity_DutyArray.substring(i * 2, i * 2 + 2);

      // DO (day off) is just an empty placeholder so ignore. Also ignore CR which is a reserve placeholder.
      if (activityCode !== 'DO' && activityCode !== 'CR') {
        dutyEvents.push({
          title: activityCode,
          start: periodStartDate.plus({ days: i }).toISODate(),
          allDay: true,
          backgroundColor: this.MISC_COLOR,
        });
      }
    }

    return dutyEvents;
  }

  /** Convert activity type (like D24) to displayed value (24). */
  private convertDisplayedActivityType(activityType: string): string {
    switch (activityType) {
      case 'D24':
        return '24';
      case 'M2D':
        return 'M2';
      case 'GLD':
        return 'G2';
      case 'X2D':
        return 'X2';
      default:
        return activityType;
    }
  }

  /** Add a duration to a start date/time to get an end time. */
  private getEndDateTime(start: string, duration: Duration): DateTime {
    const startDateTime = DateTime.fromISO(start, { zone: 'utc' });
    return startDateTime.plus(duration);
  }

  /** Convert activity record duration to Luxon duration. */
  private convertDuration(durationString: string): Duration {
    let duration = Duration.fromObject({});

    const parts = durationString.split(':');

    if (parts.length === 3) {
      const daysAndHours = parts[0].split('.');

      if (daysAndHours.length === 2) {
        // Includes days (1.08) = 1 day, 8 hours.
        duration = duration.set({
          days: parseInt(daysAndHours[0]),
          hours: parseInt(daysAndHours[1]),
          minutes: parseInt(parts[1]),
          seconds: parseInt(parts[2]),
        });
      } else {
        // Just hours.
        duration = duration.set({
          hours: parseInt(daysAndHours[0]),
          minutes: parseInt(parts[1]),
          seconds: parseInt(parts[2]),
        });
      }
    }

    return duration;
  }

  /**
   * Calculates the css class to use for offsetting the event based on time of day.
   * Event start gets left margin, event end gets right margin. Preset css classes
   * with intervals of 5px are used, and the closest applicable one is used.
   */
  private getEventMarginClass(
    eventStartTime: DateTime,
    eventEndTime: DateTime,
  ): { startClass: string; endClass: string } {
    // Don't shift event display if it's a one day trip.
    if (eventEndTime.diff(eventStartTime).as('hours') < 24) {
      return { startClass: '', endClass: '' };
    }

    // These values will need changed if the current padding or max width changes.
    const MAX_CALENDAR_WIDTH = 700;
    const PADDING = 32;

    const screenWidth = window.innerWidth;

    // The calendar will be a max of 500px wide, but can be smaller on
    // smaller screens.
    const calendarWidth = Math.min(screenWidth, MAX_CALENDAR_WIDTH);

    // The width of a single calendar day.
    const dayCellWidth = (calendarWidth - PADDING) / 7;

    const startDayRatio = eventStartTime.hour / 24;
    const endDayRatio = (24 - eventEndTime.hour) / 24; // Want the amount of day left.

    // Calculate offset and use closest multiple of 5.
    const leftMargin = Math.round((startDayRatio * dayCellWidth) / 5) * 5;
    const rightMargin = Math.round((endDayRatio * dayCellWidth) / 5) * 5;

    return {
      startClass: `left-margin-${leftMargin}`,
      endClass: `right-margin-${rightMargin}`,
    };
  }

  /** Returns an array of events that provide holiday pay. */
  private createHolidayPayEvents(month: number, year: number): EventInput[] {
    const premiumDates: EventInput[] = [];

    // For December: Christmas Eve, Christmas, New Year's Eve.
    if (month === 12) {
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-12-24`,
      });

      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-12-25`,
      });

      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-12-31`,
      });
    }

    // For January: New Year's Day.
    if (month === 1) {
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-01-01`,
      });
    }

    // July 4th.
    if (month === 7) {
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-07-04`,
      });
    }

    // Memorial Day. Need to find last Monday of May.
    if (month === 5) {
      // Iterate backward through days, Memorial day is the first Monday found.
      for (let i = 31; i > 0; i--) {
        const date = DateTime.fromObject({ year, month, day: i });

        if (date.weekday === 1) {
          premiumDates.push({
            display: 'background',
            allDay: true,
            start: `${year}-05-${date.day}`,
          });
          break;
        }
      }
    }

    // For November:
    // - Day before Thanksgiving.
    // - Thanksgiving.
    // - The Sunday following Thanksgiving Day.
    if (month === 11) {
      // Find the fourth Thursday to identify Thanksgiving.

      // Start on the first.
      let date = DateTime.fromObject({ year, month, day: 1 });

      // Find first Thursday.
      let isThursday = false;

      while (!isThursday) {
        if (date.weekday === 4) {
          isThursday = true;
        } else {
          date = date.plus({ days: 1 });
        }
      }

      const thanksgiving = date.plus({ weeks: 3 });

      // Create events.
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-${month}-${thanksgiving.minus({ days: 1 }).day}`,
      });

      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-${month}-${thanksgiving.day}`,
      });

      // Sunday after Thanksgiving can potentially be in December.
      const sunAfterThanks = thanksgiving.plus({ days: 3 });
      premiumDates.push({
        display: 'background',
        allDay: true,
        start: `${year}-${sunAfterThanks.month}-${sunAfterThanks.day
          .toString()
          .padStart(2, '0')}`,
      });
    }

    return premiumDates;
  }
}
